import { FILTER_VALUE_SEPARATOR } from '@aibees/elephant/constants/filters';
import { useSearchBrowseData } from '@aibees/elephant/context/search-browse-data-provider';
import { SuggestionSearchBarOptionBase } from '@aibees/elephant/feature/suggestion-search-bar';
import {
  useSearchModeFromRoute,
  useSearchQueryFilter,
  useSearchTypeFromRoute,
} from '@aibees/elephant/hooks';
import { useModalityChangeCounterStore } from '@aibees/elephant/store/modality-change-counter';
import { SearchType } from '@aibees/elephant/types';
import { useWorkspace } from '@aibees/shared/workspace';
import { useDeferredValue, useMemo } from 'react';
import { useBrowseCompanySearchCore } from '../../../feature/search-table-company/hooks/use-browse-company-search';
import type { CompanySearchResponse } from '../../../feature/search-table-company/hooks/types';
import { PeopleTableInfo } from '../../../feature/search-table-people';
import { useBrowsePeopleSearchCore } from '../../../feature/search-table-people/hooks/use-browse-people-search';
import type { PeopleSearchResponse } from '../../../feature/search-table-people/hooks/use-browse-people-search';
import { mapToSuggestionOption } from '../../../feature/suggestion-search-bar/utils';

function mapToDefaultValues(
  type: SearchType,
  companyData: CompanySearchResponse | undefined,
  peopleData: PeopleSearchResponse | undefined
): SuggestionSearchBarOptionBase[] {
  if (type === SearchType.Company) {
    return (
      companyData?.top
        ?.map((item) =>
          mapToSuggestionOption(
            item,
            'id',
            'summary.name',
            'link.linkedin',
            'link.linkedin',
            'summary.logo.source'
          )
        )
        .filter(Boolean) ?? []
    );
  }
  return (
    peopleData?.top
      ?.map((item) =>
        mapToSuggestionOption(
          item,
          'id',
          'profile.full_name',
          'link.linkedin',
          'link.linkedin',
          'profile.picture.source'
        )
      )
      .filter(Boolean) ?? []
  );
}

export function useDefaultValues(options?: {
  /**
   * Gates the browse fetch. Defaults to `true` (always resolve from the API).
   * `useLookalikeSeeds` passes `false` once the client-side store already covers
   * the URL, so editing chips no longer triggers a redundant round-trip.
   */
  enabled?: boolean;
}): SuggestionSearchBarOptionBase[] {
  const fetchEnabled = options?.enabled ?? true;
  const browseData = useSearchBrowseData();
  const workspace = useWorkspace()?.id;
  const type = useSearchTypeFromRoute();
  const mode = useSearchModeFromRoute();
  const { params } = useSearchQueryFilter({ select: ['value'] });
  const deferredParams = useDeferredValue(params);
  const { counter: modalityVersion } = useModalityChangeCounterStore();

  const valueArr = params.value?.split(FILTER_VALUE_SEPARATOR) ?? [];
  const useContextData = browseData != null;

  const { data: companyDataFromHook } = useBrowseCompanySearchCore(
    {},
    { workspace, type, mode, searchParams: deferredParams, modalityVersion },
    {
      enabled:
        fetchEnabled &&
        !useContextData &&
        type === SearchType.Company &&
        valueArr.length > 0,
    },
    true
  );

  const { data: peopleDataFromHook } =
    useBrowsePeopleSearchCore<PeopleTableInfo>(
      {},
      { workspace, type, searchParams: deferredParams, modalityVersion },
      {
        enabled:
          fetchEnabled &&
          !useContextData &&
          type === SearchType.People &&
          valueArr.length > 0,
      },
      true
    );

  const companyData = useContextData
    ? browseData!.companyData
    : companyDataFromHook;
  const peopleData = useContextData
    ? browseData!.peopleData
    : peopleDataFromHook;

  return useMemo<SuggestionSearchBarOptionBase[]>(
    () =>
      mapToDefaultValues(type ?? SearchType.Company, companyData, peopleData),
    [type, companyData, peopleData]
  );
}
